import Decimal from 'decimal.js';
import { TaxInclusiveOption } from '../purchase/purchase.order.line';
import { ItemSection, ItemSectionType } from '../quotes/quote.pro';
import { AdjustmentType, EmbeddedCode } from '../shared/code';
import { SaleOrder } from './sale.order';

/**
 * Named Sales Order domain calculator (EF V2 production contract).
 * Shared by Surface recompute and server totals validation — quote calculation is not authoritative.
 *
 * Rules:
 * - Tax modes: Exclusive, Inclusive, No Tax
 * - Adjustments: Value absolute then Percent-of-gross (single line adjustmentType)
 * - Rounding: line totals to 2dp, then document totals to 2dp
 * - Cost-only sections force zero sell/adjustment values
 * - Zero quantity yields zero sell totals
 * - Non-finite inputs are rejected
 * - Source model is never mutated
 */
export type SaleOrderCalculationSource = Pick<SaleOrder, 'sections' | 'taxInclusiveOption'>;

export interface SaleOrderCalculationResult {
  sections: ItemSection[];
  totalEx: number;
  totalTax: number;
  totalInc: number;
  totalAdjustment: number;
  totalBuy: number;
}

export class SaleOrderCalculationDomain {

  static calculate(source: SaleOrderCalculationSource): SaleOrderCalculationResult {

    return calculateSaleOrder(source);
  }
}

export function calculateSaleOrder(source: SaleOrderCalculationSource): SaleOrderCalculationResult {

  const taxMode = source?.taxInclusiveOption ?? TaxInclusiveOption.Exclusive;
  const sections = (source?.sections ?? []).map(section => calculateSection(section, taxMode));

  let totalEx = new Decimal(0);
  let totalTax = new Decimal(0);
  let totalInc = new Decimal(0);
  let totalAdjustment = new Decimal(0);
  let totalBuy = new Decimal(0);

  for (const section of sections) {
    totalEx = totalEx.plus(section.totalSellEx ?? 0);
    totalTax = totalTax.plus(section.totalTax ?? 0);
    totalInc = totalInc.plus(section.totalSellInc ?? 0);
    totalAdjustment = totalAdjustment.plus(section.totalAdjustment ?? 0);
    totalBuy = totalBuy.plus(section.totalBuy ?? 0);
  }

  return {
    sections,
    totalEx: round2(totalEx),
    totalTax: round2(totalTax),
    totalInc: round2(totalInc),
    totalAdjustment: round2(totalAdjustment),
    totalBuy: round2(totalBuy),
  };
}

function calculateSection(source: ItemSection | undefined, taxMode: TaxInclusiveOption): ItemSection {

  const costOnly = isCostOnlySection(source);
  const items = (source?.items ?? []).map(item => calculateItem(item, taxMode, costOnly));

  let totalBuy = new Decimal(0);
  let totalSellEx = new Decimal(0);
  let totalAdjustment = new Decimal(0);
  let totalTax = new Decimal(0);
  let totalSellInc = new Decimal(0);

  for (const item of items) {
    totalBuy = totalBuy.plus(item.totalBuy ?? 0);
    totalSellEx = totalSellEx.plus(item.totalSellEx ?? 0);
    totalAdjustment = totalAdjustment.plus(item.totalAdjustment ?? 0);
    totalTax = totalTax.plus(item.totalTax ?? 0);
    totalSellInc = totalSellInc.plus(item.totalSellInc ?? 0);
  }

  return {
    ...(source ?? {} as ItemSection),
    costOnly,
    type: source?.type ?? (costOnly ? ItemSectionType.CostOnly : ItemSectionType.Items),
    items,
    totalBuy: round2(totalBuy),
    totalSellEx: round2(totalSellEx),
    totalAdjustment: round2(totalAdjustment),
    totalTax: round2(totalTax),
    totalSellInc: round2(totalSellInc),
  };
}

function calculateItem(source: EmbeddedCode | undefined, taxMode: TaxInclusiveOption, costOnly: boolean): EmbeddedCode {

  const item: EmbeddedCode = { ...(source ?? {} as EmbeddedCode) };

  if (costOnly) {
    item.sellPrice = 0;
    item.customSellPrice = false;
    item.adjustment = 0;
  }

  const quantity = requireFinite(item.quantity ?? 0, 'quantity');
  const sellPrice = requireFinite(costOnly ? 0 : (item.sellPrice ?? 0), 'sellPrice');
  const buyPrice = requireFinite(item.buyPrice ?? 0, 'buyPrice');
  const adjustment = requireFinite(costOnly ? 0 : (item.adjustment ?? 0), 'adjustment');
  const taxRate = taxMode === TaxInclusiveOption.NoTax
    ? 0
    : requireFinite(item.saleTaxRate?.rate ?? 0, 'saleTaxRate.rate');
  const adjustmentType: AdjustmentType = item.adjustmentType === 'Percent' ? 'Percent' : 'Value';

  const totalBuy = round2(new Decimal(buyPrice).mul(quantity));
  const gross = round2(new Decimal(sellPrice).mul(quantity));

  // Value adjustment is an absolute money amount; Percent is of the line gross.
  // Both resolve to a monetary value before tax is applied.
  const totalAdjustment = adjustmentType === 'Percent'
    ? round2(new Decimal(gross).mul(adjustment).div(100))
    : round2(adjustment);

  const adjusted = round2(new Decimal(gross).plus(totalAdjustment));
  const totals = lineTaxTotals(adjusted, taxRate, taxMode);

  item.totalBuy = totalBuy;
  item.totalAdjustment = totalAdjustment;
  item.totalSellEx = totals.totalEx;
  item.totalTax = totals.totalTax;
  item.totalSellInc = totals.totalInc;
  item.adjustedSell = quantity === 0 ? 0 : round2(new Decimal(totals.totalEx).div(quantity));

  return item;
}

function lineTaxTotals(adjustedLine: number, taxRatePercent: number, taxMode: TaxInclusiveOption): { totalEx: number; totalTax: number; totalInc: number } {

  const rate = new Decimal(taxRatePercent).div(100);

  switch (taxMode) {
    case TaxInclusiveOption.Inclusive: {
      // Sell/adjustment amounts are tax-inclusive; extract tax at line precision.
      const totalInc = round2(adjustedLine);
      const divisor = rate.plus(1);
      const totalEx = divisor.isZero() ? totalInc : round2(new Decimal(totalInc).div(divisor));
      const totalTax = round2(new Decimal(totalInc).minus(totalEx));
      return { totalEx, totalTax, totalInc };
    }
    case TaxInclusiveOption.NoTax: {
      const totalEx = round2(adjustedLine);
      return { totalEx, totalTax: 0, totalInc: totalEx };
    }
    case TaxInclusiveOption.Exclusive:
    default: {
      const totalEx = round2(adjustedLine);
      const totalTax = round2(new Decimal(totalEx).mul(rate));
      const totalInc = round2(new Decimal(totalEx).plus(totalTax));
      return { totalEx, totalTax, totalInc };
    }
  }
}

function isCostOnlySection(section: ItemSection | undefined): boolean {

  return section?.costOnly === true || section?.type === ItemSectionType.CostOnly;
}

function requireFinite(value: number, field: string): number {

  const numeric = value as unknown;
  if (typeof numeric === 'string' && numeric.trim() === '') {
    return 0;
  }

  const n = Number(value);
  if (!Number.isFinite(n)) {
    throw new Error(`Sale order calculation rejected non-finite ${field}`);
  }
  return n;
}

function round2(value: Decimal.Value): number {

  return new Decimal(value ?? 0).toDP(2).toNumber();
}
